import { motion } from 'motion/react'
import { Link } from 'react-router'

import type { Product } from '../../data/products'
import { cx } from '../lib/cx'
import { hideProductWeight, useI18n } from '../lib/i18n'

export function ProductCard({ product, index = 0, priority }: { product: Product; index?: number; priority?: boolean }) {
  const { t } = useI18n()
  const nameKey = `product_name_${product.slug.replaceAll('-', '_')}`
  const shortKey = `product_short_${product.slug.replaceAll('-', '_')}`
  const translatedName = t(nameKey) === nameKey ? product.name : t(nameKey)
  const translatedShort = hideProductWeight(t(shortKey) === shortKey ? product.short : t(shortKey))
  const badgeKeys: Record<string, string> = { New: 'badge_new_label', Bestseller: 'badge_bestseller_label', 'Low stock': 'badge_low_stock_label' }

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -6% 0px' }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col"
    >
      <Link to={`/product/${product.slug}`} className="block">
        <div className="image-frame relative aspect-[4/5] rounded-image shadow-[0_18px_40px_-30px_rgba(41,37,31,0.55)] transition-shadow duration-500 group-hover:shadow-[0_28px_50px_-28px_rgba(37,75,53,0.45)]">
          <img
            src={product.image}
            alt={translatedName}
            loading={priority ? 'eager' : 'lazy'}
            className="size-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
          />
          {product.badge && (
            <span
              className={cx(
                'absolute left-3 top-3 rounded-[10px] px-2.5 py-1 text-[11.5px] font-medium tracking-[0.02em] backdrop-blur',
                product.badge === 'Low stock'
                  ? 'bg-accent-soft/95 text-accent'
                  : product.badge === 'New'
                    ? 'bg-forest/95 text-bg'
                    : 'bg-surface/95 text-forest',
              )}
            >
              {t(badgeKeys[product.badge] ?? product.badge)}
            </span>
          )}
        </div>
        <div className="mt-4">
          <div className="min-w-0">
            <h3 className="font-display truncate text-[21px] leading-tight">{translatedName}</h3>
            {translatedShort && <p className="mt-1 truncate text-[12.5px] tracking-[0.01em] text-muted">{translatedShort}</p>}
          </div>
        </div>
      </Link>
    </motion.article>
  )
}
